
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Customer, ContractCreate, PricingHeader, ContractLineCreate, PriceList } from '../types';

interface ContractCreationProps {
  customer: Customer;
  onClose: () => void;
}

const ContractCreation: React.FC<ContractCreationProps> = ({ customer, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Determine upcoming contract start date as first day of next month (UTC)
  const getUpcomingContractStartDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based
    const firstOfNextMonth = new Date(year, month + 1, 1);
    return new Date(firstOfNextMonth.getTime() - firstOfNextMonth.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  };

  // Calculate period end (YYYY-MM-DD) given a start date and billing cycle
  const getUpcomingEndDate = (
    startISO: string,
    cycle: '1_month' | '3_months' | '12_months' = '1_month'
  ) => {
    const start = new Date(`${startISO}T00:00:00Z`);
    const monthsToAdd = cycle === '12_months' ? 12 : cycle === '3_months' ? 3 : 1;
    const nextStart = new Date(start);
    nextStart.setUTCMonth(nextStart.getUTCMonth() + monthsToAdd);
    const end = new Date(nextStart);
    end.setUTCDate(end.getUTCDate() - 1);
    return end.toISOString().split('T')[0];
  };

  const createSupervisorContract = useCallback(async () => {
    setLoading(true);
    try {
      const pricingHeadersResponse = await apiService.getPricingHeaders(0, 100);
      const defaultPricingHeader = pricingHeadersResponse.find((ph: PricingHeader) => ph.is_active && ph.is_standard);

      if (!defaultPricingHeader) {
        alert('No default pricing header found. Please create a standard pricing header first.');
        onClose();
        return;
      }

      const priceLists = await apiService.getPriceLists(1, 100);
      const defaultPriceList = priceLists.items.find((pl: PriceList) =>
        pl.pricing_header_id === defaultPricingHeader.pricing_header_id &&
        pl.is_active
      );

      if (!defaultPriceList) {
        alert('No active price list found for the default pricing header.');
        onClose();
        return;
      }

      const contractData: ContractCreate = {
        contract_name: `Contract for Supervisor ${customer.customer_name}`,
        customer_id: customer.id,
        fixed_pricing_header_id: defaultPricingHeader.pricing_header_id,
        is_current_pricing_header: true,
        start_date: getUpcomingContractStartDate(),
        billing_cycle: '1_month',
        contract_length: '12_months',
        notice_period: '1_month',
        currency: customer.currency as 'USD' | 'EUR' | 'GBP' | 'BRL' | 'CAD' | 'CHF' || 'EUR',
        language: (customer.language || 'en').toLowerCase() as 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt' | 'nl' | 'pl' | 'ru' | 'ja' | 'zh',
        contract_createdby: 'system',
        isactive: true,
        isdeleted: false
      };

      const contractResponse = await apiService.createContract(contractData);
      const contractId = contractResponse.contract_id;

      const supervisorLineData: ContractLineCreate = {
        contractline_name: `Supervisor: ${customer.customer_name}`,
        contract_id: contractId,
        pricelist_id: defaultPriceList.pricelist_id,
        billing_cycle: '1_month',
        start: getUpcomingContractStartDate(),
        upcoming_billing_start: getUpcomingContractStartDate(),
        upcoming_billing_end: getUpcomingEndDate(getUpcomingContractStartDate(), '1_month'),
        is_active: true,
        trial_period: 0,
        contractline_createdby: 'system',
        isactive: true,
        isdeleted: false,
        iscurrent: true
      };

      await apiService.createContractLine(supervisorLineData);

      if (customer.supervisor_id) {
        const controllers = await apiService.getControllersBySupervisor(customer.supervisor_id);
        for (const controller of controllers) {
          const controllerLineData: ContractLineCreate = {
            contractline_name: `Controller: ${controller.controller_name}`,
            contract_id: contractId,
            pricelist_id: defaultPriceList.pricelist_id,
            billing_cycle: '1_month',
            start: getUpcomingContractStartDate(),
            upcoming_billing_start: getUpcomingContractStartDate(),
            upcoming_billing_end: getUpcomingEndDate(getUpcomingContractStartDate(), '1_month'),
            is_active: true,
            trial_period: 0,
            contractline_createdby: 'system',
            isactive: true,
            isdeleted: false,
            iscurrent: true
          };
          await apiService.createContractLine(controllerLineData);
        }
        alert(`Contract created successfully for supervisor ${customer.customer_name} with ${controllers.length} controllers`);
      } else {
        alert(`Contract created successfully for supervisor ${customer.customer_name}`);
      }
      
      navigate(`/contracts/${contractId}`);
    } catch (error: any) {
      console.error('Error creating supervisor contract:', error);
      if (error.response && error.response.status === 400) {
        alert(`Failed to create supervisor contract: ${error.response.data.detail}`);
      } else {
        alert('Failed to create supervisor contract. Please try again.');
      }
    } finally {
      setLoading(false);
      onClose();
    }
  }, [customer, navigate, onClose]);

  const createControllerContract = useCallback(async () => {
    setLoading(true);
    try {
      const pricingHeadersResponse = await apiService.getPricingHeaders(0, 100);
      const defaultPricingHeader = pricingHeadersResponse.find((ph: PricingHeader) => ph.is_active && ph.is_standard);

      if (!defaultPricingHeader) {
        alert('No default pricing header found. Please create a standard pricing header first.');
        onClose();
        return;
      }

      const priceLists = await apiService.getPriceLists(1, 100);
      const defaultPriceList = priceLists.items.find((pl: PriceList) =>
        pl.pricing_header_id === defaultPricingHeader.pricing_header_id &&
        pl.is_active
      );

      if (!defaultPriceList) {
        alert('No active price list found for the default pricing header.');
        onClose();
        return;
      }

      const contractData: ContractCreate = {
        contract_name: `Contract for ${customer.customer_name}`,
        customer_id: customer.id,
        fixed_pricing_header_id: defaultPricingHeader.pricing_header_id,
        is_current_pricing_header: true,
        start_date: getUpcomingContractStartDate(),
        billing_cycle: '1_month',
        contract_length: '12_months',
        notice_period: '1_month',
        currency: customer.currency as 'USD' | 'EUR' | 'GBP' | 'BRL' | 'CAD' | 'CHF' || 'EUR',
        language: (customer.language || 'en').toLowerCase() as 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt' | 'nl' | 'pl' | 'ru' | 'ja' | 'zh',
        contract_createdby: 'system',
        isactive: true,
        isdeleted: false
      };

      const contractResponse = await apiService.createContract(contractData);
      const contractId = contractResponse.contract_id;

      const contractLineData: ContractLineCreate = {
        contractline_name: customer.customer_name,
        contract_id: contractId,
        pricelist_id: defaultPriceList.pricelist_id,
        billing_cycle: '1_month',
        start: getUpcomingContractStartDate(),
        upcoming_billing_start: getUpcomingContractStartDate(),
        upcoming_billing_end: getUpcomingEndDate(getUpcomingContractStartDate(), '1_month'),
        is_active: true,
        trial_period: 0,
        contractline_createdby: 'system',
        isactive: true,
        isdeleted: false,
        iscurrent: true
      };

      await apiService.createContractLine(contractLineData);

      alert(`Contract created successfully for controller ${customer.customer_name}`);
      navigate(`/contracts/${contractId}`);
    } catch (error: any) {
      console.error('Error creating controller contract:', error);
      if (error.response && error.response.status === 400) {
        alert(`Failed to create controller contract: ${error.response.data.detail}`);
      } else {
        alert('Failed to create controller contract. Please try again.');
      }
    } finally {
      setLoading(false);
      onClose();
    }
  }, [customer, navigate, onClose]);

  useEffect(() => {
    if (customer.customer_type === 'supervisor') {
      createSupervisorContract();
    } else if (customer.customer_type === 'controller') {
      createControllerContract();
    }
  }, [customer, createSupervisorContract, createControllerContract]);

  return (
    <div>
      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p style={{ marginTop: '20px' }}>Creating contract for {customer.customer_name}...</p>
        </div>
      )}
    </div>
  );
};

export default ContractCreation;
